import React, { useState, useEffect } from 'react';

/**
 * PrimaryContactCard — gedeelde kaart met het primaire contact van de lead
 * uit de platte Lead-kolommen (contact_name/position/email/phone/mobile).
 *
 * Wordt bovenaan de Contacten-tab getoond in prospect-, onboarding- en
 * client-weergaven, zodat het bij de lead ingevoerde contact door de hele
 * pipeline zichtbaar blijft (de contact_methods-rijen zijn "extra" contacten).
 *
 * Props:
 * - lead:   object met de platte contactvelden (bijv. LeadResponse of _raw)
 * - leadId: optioneel — als `lead` de velden mist wordt GET /leads/{id}
 *           gebruikt (zelfde patroon als LeadGeneralInfoSection)
 * - showLabel: toon het "Primair contact (bedrijfsprofiel)"-kopje (default true)
 *
 * Rendert niets als er geen naam, e-mail of telefoonnummer bekend is.
 */

const getToken = () => sessionStorage.getItem('auth_token');

const CONTACT_FIELDS = ['contact_name', 'contact_position', 'contact_email', 'contact_phone', 'contact_mobile'];

const hasContact = (v) =>
  !!(v && (v.contact_name || v.contact_email || v.contact_phone || v.contact_mobile));

export default function PrimaryContactCard({ lead = null, leadId = null, showLabel = true, className = '' }) {
  const [fetched, setFetched] = useState(null);

  // Alleen bijladen als de aanroepende view de platte velden niet al heeft.
  useEffect(() => {
    if (!leadId || hasContact(lead)) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/v1/leads/${leadId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) {
          const out = {};
          CONTACT_FIELDS.forEach(k => { out[k] = data?.[k] ?? ''; });
          setFetched(out);
        }
      } catch { /* stil falen — kaart blijft dan verborgen */ }
    })();
    return () => { cancelled = true; };
  }, [leadId, lead]);

  const v = hasContact(lead) ? lead : fetched;
  if (!hasContact(v)) return null;

  const phone = v.contact_mobile || v.contact_phone;

  return (
    <div className={className}>
      {showLabel && (
        <p className="text-[10px] font-semibold text-[#a4abbe] uppercase tracking-wider mb-2">
          Primair contact (bedrijfsprofiel)
        </p>
      )}
      <div className="p-4 bg-[#eef2fa] rounded-xl border border-[#5a7fc2]/20">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ backgroundColor: '#3d61a4' }}
          >
            {(v.contact_name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            {v.contact_name && (
              <p className="text-sm font-semibold text-[#011745]">{v.contact_name}</p>
            )}
            {v.contact_position && (
              <p className="text-xs text-[#566079]">{v.contact_position}</p>
            )}
            {v.contact_email && (
              <a href={`mailto:${v.contact_email}`} className="flex items-center gap-1.5 text-xs text-[#3d61a4] hover:underline">
                ✉ {v.contact_email}
              </a>
            )}
            {phone && (
              <a href={`tel:${phone}`} className="flex items-center gap-1.5 text-xs text-[#00c875] hover:underline">
                📱 {phone}
              </a>
            )}
          </div>
          <span className="text-xs px-2 py-0.5 bg-white text-[#3d61a4] rounded-full font-medium border border-[#5a7fc2]/30">
            ★ Primair
          </span>
        </div>
      </div>
    </div>
  );
}
